import Box from '@amzn/awsui-components-core/box';
import Button from '@amzn/awsui-components-core/button';
import ColumnLayout from '@amzn/awsui-components-core/column-layout';
import ExpandableSection from '@amzn/awsui-components-core/expandable-section';
import FormField from '@amzn/awsui-components-core/form-field';
import Select, { SelectProps } from '@amzn/awsui-components-core/select';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import React, { useCallback, useMemo } from 'react';
import { PnlVariancePair } from '../starGridTypes';
import { formatMonth } from './ColumnsPicker';

/** Scenarios already selected in the parent form — the domain of valid pair endpoints. */
export interface AvailableScenario {
  /** Unique identifier for this column entry (disambiguates same-named scenarios). */
  readonly id: string;
  /** The scenario name (may not be unique across entries). */
  readonly label: string;
  readonly selectedMonths: readonly number[];
}

interface VariancePairsPickerProps {
  readonly availableScenarios: readonly AvailableScenario[];
  readonly pairs: readonly PnlVariancePair[];
  readonly onChange: (next: readonly PnlVariancePair[]) => void;
}

/** Return all selected months for a given scenario by its unique id. */
const scenarioMonthsById = (scenarios: readonly AvailableScenario[], id: string): number[] =>
  [...(scenarios.find((s) => s.id === id)?.selectedMonths ?? [])].sort((a, b) => a - b);

/** Build a display label for a scenario, appending month range when names are duplicated. */
const buildDisplayLabel = (scenario: AvailableScenario, allScenarios: readonly AvailableScenario[]): string => {
  const hasDuplicate = allScenarios.filter((s) => s.label === scenario.label).length > 1;
  if (!hasDuplicate || scenario.selectedMonths.length === 0) return scenario.label;
  const sorted = [...scenario.selectedMonths].sort((a, b) => a - b);
  if (sorted.length === 1) return `${scenario.label} (${formatMonth(sorted[0])})`;
  return `${scenario.label} (${formatMonth(sorted[0])} – ${formatMonth(sorted[sorted.length - 1])})`;
};

/**
 * Interactive picker for variance pairs (ported from xPT's VariancePairsPicker).
 * Each pair = base scenario with its own months, compare scenario with its own months.
 * Months are paired positionally for computation. Users add individual month-pair
 * rows to make the pairing explicit.
 */
const VariancePairsPicker: React.FC<VariancePairsPickerProps> = ({ availableScenarios, pairs, onChange }) => {
  const scenarioOptions = useMemo<SelectProps.Options>(
    () =>
      availableScenarios.map((s) => ({
        value: s.id,
        label: buildDisplayLabel(s, availableScenarios)
      })),
    [availableScenarios]
  );

  const handleAddPair = useCallback((): void => {
    if (availableScenarios.length < 2) return;
    onChange([
      ...pairs,
      {
        base_scenario: '',
        compare_scenario: '',
        base_months: [],
        compare_months: []
      }
    ]);
  }, [availableScenarios, pairs, onChange]);

  const handleRemovePair = useCallback(
    (idx: number): void => {
      onChange(pairs.filter((_, i) => i !== idx));
    },
    [pairs, onChange]
  );

  const handleFieldChange = useCallback(
    (idx: number, next: Partial<PnlVariancePair>): void => {
      const updated = pairs.map((p, i) => {
        if (i !== idx) return p;
        const merged = { ...p, ...next };
        // Reset months when scenario changes — user must re-pick pairs.
        if (next.base_scenario) merged.base_months = [];
        if (next.compare_scenario) merged.compare_months = [];
        return merged;
      });
      onChange(updated);
    },
    [pairs, onChange]
  );

  const handleAddMonthPair = useCallback(
    (pairIdx: number): void => {
      const updated = pairs.map((p, i) => {
        if (i !== pairIdx) return p;
        return { ...p, base_months: [...p.base_months, 0], compare_months: [...p.compare_months, 0] };
      });
      onChange(updated);
    },
    [pairs, onChange]
  );

  const handleRemoveMonthPair = useCallback(
    (pairIdx: number, monthIdx: number): void => {
      const updated = pairs.map((p, i) => {
        if (i !== pairIdx) return p;
        return {
          ...p,
          base_months: p.base_months.filter((_, mi) => mi !== monthIdx),
          compare_months: p.compare_months.filter((_, mi) => mi !== monthIdx)
        };
      });
      onChange(updated);
    },
    [pairs, onChange]
  );

  const handleMonthChange = useCallback(
    (pairIdx: number, monthIdx: number, side: 'base' | 'compare', value: number): void => {
      const updated = pairs.map((p, i) => {
        if (i !== pairIdx) return p;
        if (side === 'base') {
          const base_months = p.base_months.map((m, mi) => (mi === monthIdx ? value : m));
          return { ...p, base_months };
        }
        const compare_months = p.compare_months.map((m, mi) => (mi === monthIdx ? value : m));
        return { ...p, compare_months };
      });
      onChange(updated);
    },
    [pairs, onChange]
  );

  const canAddPair = availableScenarios.length >= 2;

  return (
    <SpaceBetween size="m">
      <Box variant="p" color="text-body-secondary">
        Add columns that show the difference between two scenarios. For each variance pair, add month mappings — each row pairs one base month with
        one compare month for the computation.
      </Box>

      {!canAddPair && (
        <Box variant="p" color="text-status-inactive">
          Select at least two scenarios above to configure variance columns.
        </Box>
      )}

      {pairs.map((pair, idx) => {
        const baseMonthOptions: SelectProps.Options = scenarioMonthsById(availableScenarios, pair.base_scenario).map((m) => ({
          value: String(m),
          label: formatMonth(m)
        }));
        const compareMonthOptions: SelectProps.Options = scenarioMonthsById(availableScenarios, pair.compare_scenario).map((m) => ({
          value: String(m),
          label: formatMonth(m)
        }));
        const baseDisplayLabel = availableScenarios.find((s) => s.id === pair.base_scenario)
          ? buildDisplayLabel(availableScenarios.find((s) => s.id === pair.base_scenario)!, availableScenarios)
          : pair.base_scenario || '(select)';
        const compareDisplayLabel = availableScenarios.find((s) => s.id === pair.compare_scenario)
          ? buildDisplayLabel(availableScenarios.find((s) => s.id === pair.compare_scenario)!, availableScenarios)
          : pair.compare_scenario || '(select)';

        return (
          <ExpandableSection
            key={idx}
            headerText={`Variance ${idx + 1}: ${baseDisplayLabel} − ${compareDisplayLabel} (${pair.base_months.length} pair${
              pair.base_months.length === 1 ? '' : 's'
            })`}
            defaultExpanded
          >
            <SpaceBetween size="s">
              <ColumnLayout columns={2}>
                <FormField label="Base Scenario">
                  <Select
                    selectedOption={pair.base_scenario ? { value: pair.base_scenario, label: baseDisplayLabel } : null}
                    options={scenarioOptions.filter((o): o is SelectProps.Option => 'value' in o && o.value !== pair.compare_scenario)}
                    onChange={({ detail }) =>
                      handleFieldChange(idx, {
                        base_scenario: detail.selectedOption.value ?? pair.base_scenario
                      })
                    }
                    placeholder="Choose base scenario..."
                  />
                </FormField>
                <FormField label="Compare Scenario">
                  <Select
                    selectedOption={pair.compare_scenario ? { value: pair.compare_scenario, label: compareDisplayLabel } : null}
                    options={scenarioOptions.filter((o): o is SelectProps.Option => 'value' in o && o.value !== pair.base_scenario)}
                    onChange={({ detail }) =>
                      handleFieldChange(idx, {
                        compare_scenario: detail.selectedOption.value ?? pair.compare_scenario
                      })
                    }
                    placeholder="Choose compare scenario..."
                  />
                </FormField>
              </ColumnLayout>

              {/* Month pair rows */}
              {pair.base_scenario && pair.compare_scenario && (
                <div>
                  <Box variant="awsui-key-label" padding={{ bottom: 'xxs' }}>
                    Month Pairs
                  </Box>
                  <Box variant="p" color="text-body-secondary" fontSize="body-s" padding={{ bottom: 'xs' }}>
                    Each row maps one month from {baseDisplayLabel} to one month from {compareDisplayLabel}.
                  </Box>
                  <SpaceBetween size="xs">
                    {pair.base_months.length > 0 && (
                      <SpaceBetween direction="horizontal" size="xs">
                        <Box variant="awsui-key-label" fontSize="body-s">
                          Base ({baseDisplayLabel})
                        </Box>
                        <Box variant="span"> </Box>
                        <Box variant="awsui-key-label" fontSize="body-s">
                          Compare ({compareDisplayLabel})
                        </Box>
                      </SpaceBetween>
                    )}
                    {pair.base_months.map((baseMonth, monthIdx) => {
                      const compareMonth = pair.compare_months[monthIdx] ?? 0;
                      return (
                        <SpaceBetween key={monthIdx} direction="horizontal" size="xs">
                          <FormField>
                            <Select
                              selectedOption={baseMonth ? { value: String(baseMonth), label: formatMonth(baseMonth) } : null}
                              options={baseMonthOptions}
                              onChange={({ detail }) => handleMonthChange(idx, monthIdx, 'base', Number(detail.selectedOption.value))}
                              placeholder="Base month..."
                            />
                          </FormField>
                          <Box variant="span" padding={{ top: 'xxs' }}>
                            −
                          </Box>
                          <FormField>
                            <Select
                              selectedOption={compareMonth ? { value: String(compareMonth), label: formatMonth(compareMonth) } : null}
                              options={compareMonthOptions}
                              onChange={({ detail }) => handleMonthChange(idx, monthIdx, 'compare', Number(detail.selectedOption.value))}
                              placeholder="Compare month..."
                            />
                          </FormField>
                          <Box padding={{ top: 'xxs' }}>
                            <Button iconName="close" variant="icon" onClick={() => handleRemoveMonthPair(idx, monthIdx)} />
                          </Box>
                        </SpaceBetween>
                      );
                    })}
                    <Button iconName="add-plus" onClick={() => handleAddMonthPair(idx)}>
                      Add Month Pair
                    </Button>
                  </SpaceBetween>
                </div>
              )}

              <Box>
                <Button iconName="close" onClick={() => handleRemovePair(idx)}>
                  Remove variance
                </Button>
              </Box>
            </SpaceBetween>
          </ExpandableSection>
        );
      })}

      <Box>
        <Button iconName="add-plus" onClick={handleAddPair} disabled={!canAddPair}>
          Add variance pair
        </Button>
      </Box>
    </SpaceBetween>
  );
};

export default VariancePairsPicker;
